import { Injectable } from '@angular/core';
import * as Msal from 'msal';

import { ConfigService } from '../services/config.service';

interface MsalConfig {
    clientId: string,
    authority: string,
    redirectUri: string,
    islandReadScope: string,
    b2cScopes: string[]
}

// Based on: https://github.com/Gimly/simpleAngularAzureB2C/tree/master/src/app
// For password resets: https://github.com/Azure-Samples/active-directory-b2c-javascript-msal-singlepageapp/issues/9

// Requires msal 1.2.1, v1.2.2 adds a .finally to acquireTokenSilent() which fails in combination with Angular 4 (Angular modifies the Promise type in a way that removes the .finally() function).
// Error message: ERROR TypeError: "(new Promise(...)).then(...).catch(...).finally is not a function"

@Injectable()
export class MsalService {
    private clientApplication: Msal.UserAgentApplication;
    private signInAuthority: string;
    private editProfileAuthority: string;
    private passwordResetAuthority: string;
    private config: MsalConfig

    constructor(configService: ConfigService) {
        this.config = configService.config;
        this.signInAuthority = configService.config.authority;
        this.editProfileAuthority = configService.config.authorityToEditProfile;
        this.passwordResetAuthority = configService.config.authorityToResetPassword;

        // Docs: https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications
        var msalConfig: Msal.Configuration = {
            auth: {
                clientId: this.config.clientId,
                authority: this.config.authority,
                validateAuthority: false,
                // Default redirect to current url, but then each url would need to be configured in Azure AD
                redirectUri: this.config.redirectUri,
                // By default, msal tries to take you back to the loginStartPage after successful authentication. To disable this setting, you can pass navigateToLoginRequestUrl:false. In that case, msal will just set the url hash to null and call the provided callback, thereby avoiding an additional reload.
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: "localStorage",
                // This flag was introduced in MSAL.js v0.2.2 as a fix for the authentication loop issues on Microsoft Internet Explorer and Microsoft Edge. Enable the flag storeAuthStateInCookie to true to take advantage of this fix.
                storeAuthStateInCookie: true
            }           
        };

        this.clientApplication = new Msal.UserAgentApplication(msalConfig);
        // (optional when using redirect methods) register redirect call back for Success or Error
        this.clientApplication.handleRedirectCallback(this.authCallback);
    }

    get authenticated(): boolean {
        return this.clientApplication.getAccount() != null;
    }

    get username(): string {
        if (this.authenticated) {
            return this.clientApplication.getAccount().name;
        } else {
            return "";
        }
    }

    get userid(): string {
        if (this.authenticated) {
            return this.clientApplication.getAccount().accountIdentifier;
        } else {
            return "";
        }
    }

    public login(): void {
        this.clientApplication.loginRedirect({
            scopes: this.config.b2cScopes
        });
    }

    public editProfile(): void {
        this.clientApplication.loginRedirect({
            authority: this.editProfileAuthority,
            scopes: this.config.b2cScopes
        });
    }

    public logout(): void {
        this.clientApplication.logout();
    }

    public getToken(): Promise<string> {        
        var loginParameters: Msal.AuthenticationParameters = {
            authority: this.signInAuthority,
            scopes: this.config.b2cScopes
        };
        // Scope of just clientid refreshes idtoken when needed (https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/97#issuecomment-524494507)
        var tokenParameters: Msal.AuthenticationParameters = {
            authority: this.signInAuthority,
            scopes: [ this.config.clientId ]
        };
        return this.clientApplication.acquireTokenSilent(tokenParameters)
            .then(response => {
                console.log('Got silent access token: ', response);
                return response.idToken.rawIdToken;
            }).catch(error => {
                console.log('Could not silently retrieve token from storage.', error);
                return this.clientApplication.acquireTokenPopup(tokenParameters)
                    .then(response => {
                        console.log('Got popup access token: ', response);
                        return Promise.resolve(response.idToken.rawIdToken);
                    }).catch(innererror => {
                        console.log('Could not retrieve token from popup, using redirect', innererror);
                        this.clientApplication.loginRedirect(loginParameters);
                        return Promise.resolve('');
                    });
            });
    }

    private authCallback(error: Msal.AuthError, response?: Msal.AuthResponse) {
        if (response) {
            console.log(`Got Token: ${response.tokenType} - idToken: {${response.idToken}} - accessToken {${response.accessToken}}`);
        } else if (error) {
            console.log(`Error: ${error.errorCode} - ${error.errorMessage}`);
            if (error.errorMessage.indexOf("AADB2C90118") > -1) {
                this.clientApplication.loginRedirect({
                    authority: this.passwordResetAuthority,
                    scopes: this.config.b2cScopes
                });
            }
            else {
                console.log(`Error: ${error}`);
            }
        } else {
            console.log('No token and no error.');
        }
    }
}


// WEBPACK FOOTER //
// ./ClientApp/app/_shared/services/msal.service.ts